class ImagePrefetchService {
  private prefetchQueue: string[] = [];
  private isProcessing = false;
  private processedUrls = new Set<string>();
  private failedUrls = new Set<string>();

  public isComplete(): boolean {
    return !this.isProcessing && this.prefetchQueue.length === 0;
  }

  private async prefetchImage(url: string): Promise<void> {
    if (this.processedUrls.has(url) || this.failedUrls.has(url)) return;

    return new Promise((resolve) => {
      const img = new Image();
      img.src = url;

      img.onload = () => {
        this.processedUrls.add(url);
        resolve();
      };

      img.onerror = () => {
        this.failedUrls.add(url);
        resolve();
      };

      // Timeout de 10 segundos
      setTimeout(() => {
        if (!this.processedUrls.has(url)) {
          this.failedUrls.add(url);
          resolve();
        }
      }, 10000);
    });
  }

  private async processQueue() {
    if (this.isProcessing || this.prefetchQueue.length === 0) return;

    this.isProcessing = true;
    const BATCH_SIZE = 3;
    const DELAY_BETWEEN_BATCHES = 500;

    try {
      while (this.prefetchQueue.length > 0) {
        const batch = this.prefetchQueue.splice(0, BATCH_SIZE);

        await Promise.all(batch.map((url) => this.prefetchImage(url)));

        if (this.prefetchQueue.length > 0) {
          await new Promise((resolve) =>
            setTimeout(resolve, DELAY_BETWEEN_BATCHES)
          );
        }
      }
    } catch (error) {
      console.error("❌ Erro no processamento da fila:", error);
    } finally {
      this.isProcessing = false;
    }
  }

  public addToPrefetchQueue(urls: string[]) {
    const newUrls = urls.filter(
      (url) =>
        !this.processedUrls.has(url) &&
        !this.failedUrls.has(url) &&
        !this.prefetchQueue.includes(url)
    );

    if (newUrls.length > 0) {
      this.prefetchQueue.push(...newUrls);
      this.processQueue();
    }
  }

  public getPrefetchedUrls(): Set<string> {
    return this.processedUrls;
  }

  public getFailedUrls(): Set<string> {
    return this.failedUrls;
  }

  public getQueueStatus() {
    return {
      queueLength: this.prefetchQueue.length,
      processedCount: this.processedUrls.size,
      failedCount: this.failedUrls.size,
      isProcessing: this.isProcessing,
    };
  }
}

export const imagePrefetchService = new ImagePrefetchService();
